import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronDown, Lock, RefreshCcw, ShieldCheck } from 'lucide-react';
import PublicSiteShell, { Reveal, registroHref, whatsappHref } from '../components/public/PublicSiteShell';
import { formatPlanPrice } from '../data/publicSite';
import { usePublicPlans } from '../hooks/usePublicPlans';
import { LAUNCH_OFFER, getLaunchLabel, getLaunchPrice, getLaunchSummary, isLaunchOfferActive } from '../data/billingBrandConfig';

const ANNUAL_PCT = 0.3;  // 30% OFF pagando anual

const trustItems = [
  { icon: ShieldCheck, label: 'Sin permanencia mínima' },
  { icon: Lock, label: 'Datos protegidos y con respaldo diario' },
  { icon: RefreshCcw, label: 'Cambiás de plan cuando quieras' },
];

// Las filas se derivan de los módulos reales de cada plan (usePublicPlans),
// así la comparativa nunca promete algo que el plan no habilita.
const featureRows = [
  { label: 'Punto de venta y caja', modules: ['ventas-pos', 'caja'] },
  { label: 'Stock y productos', modules: ['inventario', 'productos'] },
  { label: 'Compras y proveedores', modules: ['compras', 'compras-nueva', 'proveedores'] },
  { label: 'Facturación AFIP/ARCA', modules: ['facturacion'] },
  { label: 'Tesorería y bancos', modules: ['bancos', 'tesoreria-cobranzas', 'tesoreria-pagos'] },
  { label: 'Producción y recetas', modules: ['produccion-registrar', 'recetas'] },
  { label: 'Flujo de fondos', modules: ['flujo-fondos'] },
  { label: 'Rentabilidad y costos', modules: ['rentabilidad-ventas', 'gestion-costos'] },
];

const supportByIndex = ['WhatsApp', 'Prioritario', 'Prioritario + asesor'];

const faqs = [
  { q: '¿Cómo es el precio de lanzamiento?', a: `Las cuentas creadas hasta el ${LAUNCH_OFFER.untilLabel} tienen ${getLaunchSummary(formatPlanPrice)}, sin códigos ni trámites. Después cada plan pasa a su precio de lista.` },
  { q: '¿Qué diferencia hay entre pagar mensual y anual?', a: 'El plan anual tiene 30% OFF sobre el precio de lista, facturado una vez al año.' },
  { q: '¿Puedo cancelar cuando quiera?', a: 'Sí, no hay permanencia mínima. Podés dar de baja tu cuenta desde la configuración cuando lo necesites.' },
  { q: '¿El IVA está incluido?', a: 'Los precios publicados son + IVA, como corresponde a la facturación de servicios en Argentina.' },
  { q: '¿Qué pasa si me quedo sin operaciones antes de fin de mes?', a: 'Te avisamos con anticipación para que puedas subir de plan sin interrumpir tu operación.' },
];

export default function Precios() {
  const navigate = useNavigate();
  const { plans, trialDays, loading: plansLoading } = usePublicPlans();
  const [billingMode, setBillingMode] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(0);

  const hasModule = (plan, ids) => ids.some((id) => (plan.modules || []).includes(id));

  return (
    <PublicSiteShell
      footerCta={{
        title: 'Probá Gastify gratis y elegí después.',
        description: isLaunchOfferActive()
          ? `${trialDays} días con todo habilitado, sin tarjeta. Lanzamiento: ${getLaunchSummary(formatPlanPrice)}.`
          : `${trialDays} días con todo habilitado, sin tarjeta.`,
        primaryLabel: 'Empezar prueba',
        primaryHref: registroHref,
        secondaryLabel: 'Hablar con un asesor',
        secondaryHref: whatsappHref,
        secondaryExternal: true,
      }}
    >
      <div className="gst-container">
        <header className="pg-header center">
          <div className="gst-eyebrow gst-eyebrow-mint">Precios</div>
          <h1>Un sistema que crece con tu negocio.</h1>
          <p>
            {trialDays} días de prueba gratis, sin tarjeta. Activás cuando estés listo.
            {isLaunchOfferActive() && ` Lanzamiento: ${getLaunchSummary(formatPlanPrice)}, para altas hasta el ${LAUNCH_OFFER.untilLabel}.`}
          </p>
          <p style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: 'var(--gst-mint-700)' }}>
            Empezá con el plan más chico para no gastar de más: a medida que tu negocio crece,
            subís de plan cuando lo necesitás, sin perder tu configuración.
          </p>

          <div className="toggle-row">
            <div className="gst-toggle">
              <button type="button" className={billingMode === 'monthly' ? 'active' : ''} onClick={() => setBillingMode('monthly')}>
                Mensual
              </button>
              <button type="button" className={billingMode === 'annual' ? 'active' : ''} onClick={() => setBillingMode('annual')}>
                Anual <span className="gst-save-chip">30% OFF</span>
              </button>
            </div>
          </div>
        </header>

        <div className="plans-grid" style={{ padding: '40px 0 24px' }}>
          {plans.map((plan, index) => {
            const featured = index === 1;
            const price = plan.price_monthly;
            const promo = getLaunchPrice(plan.key, price);
            const annualEq = Math.round(price * (1 - ANNUAL_PCT));
            const annualTotal = annualEq * 12;
            return (
              <article className={`gst-plan${featured ? ' featured' : ''}`} key={plan.key}>
                {featured && <div className="gst-plan-ribbon">Más elegido</div>}
                <div className="gst-eyebrow">{plan.badge}</div>
                <h3 style={{ marginTop: 14, fontSize: 24, fontWeight: 900 }}>{plan.marketingName}</h3>
                <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--gst-graphite-700)' }}>
                  {plan.audience || plan.summary}
                </p>

                <div style={{ margin: '20px 0 4px', minHeight: 76 }}>
                  {plansLoading || price <= 0 ? (
                    <span className="shimmer" style={{ width: 180, height: 40, borderRadius: 10 }} />
                  ) : billingMode === 'monthly' ? (
                    promo ? (
                      <>
                        <span className="gst-plan-price-old">{formatPlanPrice(price)}</span>
                        <div className="gst-plan-price">{formatPlanPrice(promo)}<small>/mes + IVA</small></div>
                        <div className="promo-note">
                          {getLaunchLabel(plan.key)} · después {formatPlanPrice(price)}/mes
                        </div>
                      </>
                    ) : (
                      <div className="gst-plan-price">{formatPlanPrice(price)}<small>/mes + IVA</small></div>
                    )
                  ) : (
                    <>
                      <span className="gst-plan-price-old">{formatPlanPrice(price)}/mes</span>
                      <div className="gst-plan-price">{formatPlanPrice(annualEq)}<small>/mes</small></div>
                      <div className="annual-note">Facturado {formatPlanPrice(annualTotal)}/año · 30% OFF</div>
                    </>
                  )}
                </div>

                <div className="plan-ops">{plan.operationLabel}</div>

                <ul style={{ marginTop: 18, flex: 1 }}>
                  {(plan.highlights || []).map((f) => (
                    <li key={f}><CheckCircle2 size={16} />{f}</li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={`gst-btn ${featured ? 'gst-btn-primary' : 'gst-btn-ghost'} gst-btn-block`}
                  style={{ marginTop: 22 }}
                  onClick={() => navigate(`${registroHref}?plan=${plan.key}&billing=${billingMode}`)}
                >
                  Empezar prueba
                </button>
              </article>
            );
          })}
        </div>

        <div className="trust-strip">
          {trustItems.map(({ icon: Icon, label }) => (
            <div className="trust-item" key={label}><Icon size={18} /> {label}</div>
          ))}
        </div>
      </div>

      <section className="gst-section-alt">
        <div className="gst-container">
          <Reveal className="compare" as="section">
            <h2>Comparar todo lo que incluye cada plan</h2>
            <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <table className="compare-table" style={{ minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Capacidad</th>
                    {plans.map((p) => <th key={p.key}>{p.marketingName}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Operaciones por mes</td>
                    {plans.map((p) => (
                      <td key={p.key}>{p.operation_limit > 0 ? p.operation_limit.toLocaleString('es-AR') : 'Ilimitadas'}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Usuarios</td>
                    {plans.map((p) => <td key={p.key}>{p.max_users}</td>)}
                  </tr>
                  {featureRows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      {plans.map((p) => (
                        <td key={p.key}>{hasModule(p, row.modules) ? <CheckCircle2 size={18} /> : '—'}</td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td>Soporte</td>
                    {plans.map((p, i) => <td key={p.key}>{supportByIndex[i] || 'WhatsApp'}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="gst-container">
        <section className="faq-section">
          <Reveal className="faq-head center">
            <div className="gst-eyebrow gst-eyebrow-mint" style={{ marginBottom: 12 }}>Preguntas frecuentes</div>
            <h2>Dudas comunes sobre precios y facturación.</h2>
          </Reveal>
          <div className="faq-list center">
            {faqs.map((f, i) => (
              <div className={`faq-item${openFaq === i ? ' open' : ''}`} key={f.q}>
                <button type="button" className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  <h4>{f.q}</h4><ChevronDown size={20} />
                </button>
                <div className="faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </PublicSiteShell>
  );
}
